import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * How many of a run of names fit on one line — so the cut can fall BETWEEN
 * them rather than through one.
 *
 * WHY THIS IS NOT CSS. `text-overflow: ellipsis` cuts by character: two dogs in
 * a narrow cell come out "Baxter · Ro…", which names one dog and half of
 * another. Sir's rule is that it should read "Baxter…" — whole names, then the
 * mark that says there are more (2026-08-27). No stylesheet can express that,
 * because nothing in CSS knows where one name ends and the next begins.
 *
 * HOW IT AVOIDS OSCILLATING. The obvious version — render fewer names, remeasure
 * — feeds its own input: the line shrinks to its content, so dropping a name can
 * make room for it again, and the cell flickers between two states for ever.
 * This measures a HIDDEN copy holding every name, against the width of the box
 * that is NOT content-sized, and re-measures only when that box changes size.
 * The visible run is then derived once and never observed.
 *
 * Returns the count to draw and the two refs to place: `boxRef` on the element
 * whose width is the constraint, `probeRef` on the hidden full-length copy.
 */
export function useFittedNames(count: number, key: string) {
  const boxRef = useRef<HTMLElement | null>(null);
  const probeRef = useRef<HTMLElement | null>(null);
  const [fit, setFit] = useState(count);

  // Layout effect: the fitted count must be settled before the browser paints,
  // or the row shows every name for a frame and then snaps.
  const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

  useIsomorphicLayoutEffect(() => {
    const box = boxRef.current;
    const probe = probeRef.current;
    if (!box || !probe) return;

    const measure = () => {
      const available = box.clientWidth;
      // Nothing to decide before layout has run, and a zero-width box would
      // otherwise settle the count at one and stay there.
      if (available <= 0) return;
      const units = [...probe.children] as HTMLElement[];
      if (units.length === 0) return;
      const origin = probe.getBoundingClientRect().left;
      let fits = 0;
      for (const unit of units) {
        // The ellipsis has to go somewhere too, so every name but the last is
        // measured against a line one ellipsis short.
        const room = fits === units.length - 1 ? available : available - ELLIPSIS;
        if (unit.getBoundingClientRect().right - origin <= room) fits += 1;
        else break;
      }
      // ALWAYS ONE. A first name too long for the cell is cut by the
      // stylesheet's own ellipsis — "Bartholomew" has no separator to fall at,
      // and half a name is better than none of it.
      setFit(Math.max(1, fits));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [count, key]);

  return { fit: Math.min(fit, count), boxRef, probeRef };
}

// The width the ellipsis needs at the desk's name size. Measured rather than
// guessed would be better; it is one glyph, and being a pixel generous costs
// nothing while being a pixel short costs the cut it was reserving room for.
const ELLIPSIS = 12;
